import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Plus,
  Eye,
  Edit2,
  Trash2,
  Package,
  Boxes,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Download,
  Upload,
  X,
  MoreVertical,
  TrendingUp,
  TrendingDown,
  RotateCcw,
  FileText,
} from "lucide-react";
import {
  getInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  getAllProducts,
  addStockMovement,
  createSupplier,
  createPurchaseOrder,
  createSupplierInvoice,
  getSupplierByName,
  getSuppliers,
  bulkInitializeInventory,
  getSkusWithoutInventory,
} from "../services/api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import toast from "react-hot-toast";
import InventoryItemForm from "../components/InventoryItemForm";
import InventoryMovementLog from "../components/InventoryMovementLog";
import InventoryDetailsModal from "../components/InventoryDetailsModal";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { useAuth } from "../context/AuthContext";

const TEMPLATE_HEADERS = [
  "Item Code*",
  "SKU Code*",
  "Barcode",
  "Item Name*",
  "Brand / Manufacturer",
  "HSN / Tariff Code",
  "Quantity Received*",
  "Unit of Measure (UOM)",
  "Pack Size",
  "Date Received",
  "Receiving Location",
  "Received By",
  "Supplier Delivery Note / GRN No.",
  "Our Purchase Order Number",
  "Supplier Code",
  "Supplier Name",
  "Supplier Invoice Number",
  "Supplier Invoice Date",
  "Unit Cost",
  "Currency",
  "Tax Rate (%)",
  "GST Amount",
  "Total Procurement Value",
  "Batch Number",
  "Manufacturing Date",
  "Expiry Date",
  "Stock Status",
  "Storage Conditions",
  "Bin / Rack Location",
  "Procurement Remarks",
  "Attachments",
];

async function downloadTemplate() {
  try {
    // Fetch all products with their SKUs
    const products = await getAllProducts();

    if (!products || products.length === 0) {
      alert("No products found in the system. Please add products first.");
      return;
    }

    // Generate template data from existing products
    const templateData = [];

    products.forEach((product) => {
      if (product.skus && product.skus.length > 0) {
        // Create a row for each SKU
        product.skus.forEach((sku) => {
          templateData.push({
            "Item Code*": product.itemCode || "",
            "SKU Code*": sku.skuCode || "",
            Barcode: sku.barcode || "",
            "Item Name*": sku.name || product.name || "",
            "Brand / Manufacturer": product.brand || "",
            "HSN / Tariff Code": product.hsnCode || "",
            "Quantity Received*": 10, // Default quantity - user should modify this
            "Unit of Measure (UOM)": product.uom || "",
            "Pack Size": sku.packSize || product.packSize || "",
            "Date Received": "",
            "Receiving Location": "",
            "Received By": "",
            "Supplier Delivery Note / GRN No.": "",
            "Our Purchase Order Number": "",
            "Supplier Code": "",
            "Supplier Name": "",
            "Supplier Invoice Number": "",
            "Supplier Invoice Date": "",
            "Unit Cost": sku.costPrice || product.costPrice || "",
            Currency: "INR",
            "Tax Rate (%)": product.gstPercentage || "",
            "GST Amount": "",
            "Total Procurement Value": "",
            "Batch Number": "",
            "Manufacturing Date": "",
            "Expiry Date": "",
            "Stock Status": "Available",
            "Storage Conditions": "",
            "Bin / Rack Location": "",
            "Procurement Remarks": "",
            Attachments: "",
          });
        });
      } else {
        // If no SKUs, create a row with product info only
        templateData.push({
          "Item Code*": product.itemCode || "",
          "SKU Code*": "", // User needs to add SKU
          Barcode: "",
          "Item Name*": product.name || "",
          "Brand / Manufacturer": product.brand || "",
          "HSN / Tariff Code": product.hsnCode || "",
          "Quantity Received*": 10, // Default quantity - user should modify this
          "Unit of Measure (UOM)": product.uom || "",
          "Pack Size": product.packSize || "",
          "Date Received": "",
          "Receiving Location": "",
          "Received By": "",
          "Supplier Delivery Note / GRN No.": "",
          "Our Purchase Order Number": "",
          "Supplier Code": "",
          "Supplier Name": "",
          "Supplier Invoice Number": "",
          "Supplier Invoice Date": "",
          "Unit Cost": product.costPrice || "",
          Currency: "INR",
          "Tax Rate (%)": product.gstPercentage || "",
          "GST Amount": "",
          "Total Procurement Value": "",
          "Batch Number": "",
          "Manufacturing Date": "",
          "Expiry Date": "",
          "Stock Status": "Available",
          "Storage Conditions": "",
          "Bin / Rack Location": "",
          "Procurement Remarks": "",
          Attachments: "",
        });
      }
    });

    // Create workbook with multiple sheets
    const wb = XLSX.utils.book_new();

    // Main template sheet
    const ws = XLSX.utils.json_to_sheet(templateData);
    XLSX.utils.book_append_sheet(wb, ws, "Inventory Template");

    // Generate SKU codes description dynamically
    const skuCodesDescription = products
      .slice(0, 10) // Show first 10 products as examples
      .map((p) => {
        const skuCodes = p.skus?.map((s) => s.skuCode).join(", ") || "No SKUs";
        return `${p.itemCode} (${skuCodes})`;
      })
      .join(", ");
    const skuNoteText = `This template contains ${templateData.length} SKU codes from ${products.length} products. Examples: ${skuCodesDescription}${products.length > 10 ? "..." : ""}`;

    // Instructions sheet
    const instructions = [
      {
        Field: "IMPORTANT NOTE",
        Required: "N/A",
        Type: "Info",
        Description: skuNoteText,
      },
      {
        Field: "Item Code*",
        Required: "Yes",
        Type: "Text",
        Description: `Product code from the product master (must exist). Examples: ${products
          .slice(0, 3)
          .map((p) => p.itemCode)
          .join(", ")}`,
      },
      {
        Field: "SKU Code*",
        Required: "Yes",
        Type: "Text",
        Description: `Specific SKU code for the product variant (pre-filled from product master). Examples: ${
          products[0]?.skus
            ?.slice(0, 3)
            .map((s) => s.skuCode)
            .join(", ") || "See template sheet"
        }. DO NOT modify unless creating custom entries.`,
      },
      {
        Field: "Barcode",
        Required: "No",
        Type: "Text",
        Description:
          "Barcode for the specific SKU (optional). Used for scanning and additional identification.",
      },
      {
        Field: "Item Name*",
        Required: "Yes",
        Type: "Text",
        Description:
          "Name of the product/item. Should match the SKU name for best results.",
      },
      {
        Field: "Brand / Manufacturer",
        Required: "No",
        Type: "Text",
        Description:
          "Brand or manufacturer information (auto-populated from Product Master if available)",
      },
      {
        Field: "HSN / Tariff Code",
        Required: "No",
        Type: "Text",
        Description:
          "HSN classification code for tax purposes (auto-populated from Product Master if available)",
      },
      {
        Field: "Quantity Received*",
        Required: "Yes",
        Type: "Number",
        Description:
          "Quantity received in this shipment. IMPORTANT: Update the default value (10) with the actual quantity received. Must be greater than 0.",
      },
      {
        Field: "Unit of Measure (UOM)",
        Required: "No",
        Type: "Text",
        Description:
          "Unit of measurement (e.g., Liters, Pieces, Kg) (auto-populated from Product Master if available)",
      },
      {
        Field: "Pack Size",
        Required: "No",
        Type: "Text",
        Description:
          "Package size information (e.g., 500ml, 1L) (auto-populated from Product Master if available)",
      },
      {
        Field: "Date Received",
        Required: "No",
        Type: "Date",
        Description: "Date when goods were received (YYYY-MM-DD format)",
      },
      {
        Field: "Receiving Location",
        Required: "No",
        Type: "Text",
        Description: "Warehouse or location where goods were received",
      },
      {
        Field: "Received By",
        Required: "No",
        Type: "Text",
        Description: "Name of person who received the goods",
      },
      {
        Field: "Supplier Delivery Note / GRN No.",
        Required: "No",
        Type: "Text",
        Description:
          "Supplier delivery note or Goods Received Note number (FROM supplier)",
      },
      {
        Field: "Our Purchase Order Number",
        Required: "No",
        Type: "Text",
        Description:
          "Our purchase order number sent TO supplier (NOT customer orders)",
      },
      {
        Field: "Supplier Code",
        Required: "No",
        Type: "Text",
        Description:
          "Unique code for the supplier (will be auto-generated if not provided, or existing supplier will be found by code)",
      },
      {
        Field: "Supplier Name",
        Required: "No",
        Type: "Text",
        Description:
          "Name of the supplier/vendor we purchased from (existing supplier will be found by name if code not provided)",
      },
      {
        Field: "Supplier Invoice Number",
        Required: "No",
        Type: "Text",
        Description:
          "Invoice number FROM supplier (NOT our invoices to customers)",
      },
      {
        Field: "Supplier Invoice Date",
        Required: "No",
        Type: "Date",
        Description:
          "Date of supplier invoice FROM supplier (YYYY-MM-DD format)",
      },
      {
        Field: "Unit Cost",
        Required: "No",
        Type: "Number",
        Description:
          "Cost per unit (will use Product Master pricing if not provided)",
      },
      {
        Field: "Currency",
        Required: "No",
        Type: "Text",
        Description:
          "Currency code (e.g., USD, INR, EUR). Defaults to INR if not specified.",
      },
      {
        Field: "Tax Rate (%)",
        Required: "No",
        Type: "Number",
        Description:
          "Tax rate percentage (will use Product Master GST percentage if not provided)",
      },
      {
        Field: "GST Amount",
        Required: "No",
        Type: "Number",
        Description:
          "GST amount for this line item (required for supplier-wise input tax reporting)",
      },
      {
        Field: "Total Procurement Value",
        Required: "No",
        Type: "Number",
        Description:
          "Total procurement value including tax (what we paid to supplier)",
      },
      {
        Field: "Batch Number",
        Required: "No",
        Type: "Text",
        Description:
          "Unique batch identifier (multiple batches supported for same SKU code)",
      },
      {
        Field: "Manufacturing Date",
        Required: "No",
        Type: "Date",
        Description: "Date of manufacture (YYYY-MM-DD format)",
      },
      {
        Field: "Expiry Date",
        Required: "No",
        Type: "Date",
        Description: "Expiration date (YYYY-MM-DD format)",
      },
      {
        Field: "Stock Status",
        Required: "No",
        Type: "Text",
        Description:
          "Current stock status (e.g., Available, Reserved, Damaged)",
      },
      {
        Field: "Storage Conditions",
        Required: "No",
        Type: "Text",
        Description: "Special storage requirements",
      },
      {
        Field: "Bin / Rack Location",
        Required: "No",
        Type: "Text",
        Description: "Specific bin or rack location within warehouse",
      },
      {
        Field: "Procurement Remarks",
        Required: "No",
        Type: "Text",
        Description:
          "Additional procurement remarks or comments about supplier transaction",
      },
      {
        Field: "Attachments",
        Required: "No",
        Type: "Text",
        Description: "File names of attached documents (comma-separated)",
      },
    ];
    const wsInstructions = XLSX.utils.json_to_sheet(instructions);
    XLSX.utils.book_append_sheet(wb, wsInstructions, "Instructions");

    // Add important notes sheet
    const notes = [
      {
        Topic: "SKU Validation",
        Explanation:
          "SKU Code is now STRICTLY validated. If a SKU code is provided but not found in the Product Master, the entire row will be SKIPPED and not imported. This ensures data integrity and prevents invalid inventory entries.",
      },
      {
        Topic: "Multiple Batch Support",
        Explanation:
          "The same SKU code can have multiple batch numbers. Each batch is treated as a separate inventory item, allowing for proper batch tracking and management.",
      },
      {
        Topic: "Supplier Lookup Enhancement",
        Explanation:
          "Supplier lookup now follows a priority order: 1) Find by Supplier Code (exact match), 2) Find by Supplier Name (exact match), 3) Create new supplier if neither exists. This prevents duplicate suppliers and ensures data consistency.",
      },
      {
        Topic: "Product Master Integration",
        Explanation:
          "Product information (Brand, HSN Code, UOM, Pack Size, Cost Price, GST Percentage) is automatically retrieved from the Product Master when available. Import data is used as fallback when Product Master data is not available.",
      },
      {
        Topic: "GST Amount for Input Tax Reporting",
        Explanation:
          "GST Amount field is crucial for supplier-wise input tax reporting. This field should be populated with the actual GST amount paid to the supplier for proper tax compliance and reporting.",
      },
      {
        Topic: "Skipped Items Tracking",
        Explanation:
          "Items that are skipped due to missing SKU codes are tracked and reported. After import completion, you will receive a detailed summary showing successful imports, failures, and skipped items with reasons.",
      },
      {
        Topic: "Required Fields",
        Explanation:
          "Item Code*, SKU Code*, Item Name*, and Quantity Received* are mandatory fields. All other fields are optional and will use Product Master data as fallback when available.",
      },
      {
        Topic: "Data Consistency",
        Explanation:
          "The system ensures data consistency by validating SKU codes against the Product Master and using Product Master information to populate missing fields in the inventory import.",
      },
    ];
    const wsNotes = XLSX.utils.json_to_sheet(notes);
    XLSX.utils.book_append_sheet(wb, wsNotes, "Important Notes");

    XLSX.writeFile(wb, "inventory_import_template.xlsx");
  } catch (error) {
    console.error("Error generating template:", error);
    alert("Failed to generate template. Please try again.");
  }
}

// Export inventory to PDF
const exportToPDF = async (inventoryItems) => {
  try {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text("Inventory Report - All Items", 14, 22);

    // Add date and summary info
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    const now = new Date();
    doc.text(`Generated: ${now.toLocaleString()}`, 14, 30);

    // Calculate summary stats
    const totalItems = inventoryItems.length;
    const totalQuantity = inventoryItems.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0,
    );
    const totalValue = inventoryItems.reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.unitCost || 0),
      0,
    );

    doc.text(
      `Total Items: ${totalItems} | Total Quantity: ${totalQuantity.toLocaleString()} | Total Value: ₹${totalValue.toLocaleString()}`,
      14,
      36,
    );

    // Prepare table data
    const tableData = inventoryItems.map((item) => [
      item.productSku?.product?.name || "-",
      item.productSku?.product?.itemCode || "-",
      item.productSku?.sku || "-",
      item.quantity?.toLocaleString() || "0",
      item.locationCode || item.location || "-",
      item.batchNumber || "-",
      item.unitCost ? `₹${parseFloat(item.unitCost).toFixed(2)}` : "-",
      item.unitCost && item.quantity
        ? `₹${(parseFloat(item.unitCost) * item.quantity).toFixed(2)}`
        : "-",
      item.stockStatus || "-",
    ]);

    // Add table
    autoTable(doc, {
      startY: 42,
      head: [
        [
          "Product Name",
          "Item Code",
          "SKU Code",
          "Quantity",
          "Location",
          "Batch",
          "Unit Cost",
          "Total Value",
          "Status",
        ],
      ],
      body: tableData,
      theme: "striped",
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 20 },
        2: { cellWidth: 20 },
        3: { cellWidth: 15, halign: "right" },
        4: { cellWidth: 20 },
        5: { cellWidth: 15 },
        6: { cellWidth: 18, halign: "right" },
        7: { cellWidth: 20, halign: "right" },
        8: { cellWidth: 15, halign: "center" },
      },
      margin: { left: 14, right: 14 },
    });

    // Add footer with totals
    const finalY = doc.lastAutoTable.finalY || 42;
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text(
      `Summary: ${totalItems} items, ${totalQuantity.toLocaleString()} total quantity, ₹${totalValue.toLocaleString()} total value`,
      14,
      finalY + 10,
    );

    // Save the PDF
    const fileName = `inventory_report_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}.pdf`;
    doc.save(fileName);

    toast.success(`PDF exported successfully! (${totalItems} items)`);
  } catch (error) {
    console.error("Error exporting PDF:", error);
    toast.error("Failed to export PDF");
  }
};

const InventoryDashboard = () => {
  const navigate = useNavigate();
  const { isAdmin, isSalesExecutive } = useAuth();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search states
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Pagination and Sorting State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "productName",
    direction: "asc",
  });
  const [importedRows, setImportedRows] = useState([]);
  const [importError, setImportError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState({
    total: 0,
    current: 0,
    success: 0,
    created: 0,
    updated: 0,
    skipped: 0,
    errors: 0,
    errorDetails: [],
    successDetails: [],
  });
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [clearBeforeImport, setClearBeforeImport] = useState(false);
  const [clearOptions, setClearOptions] = useState({
    inventory: true,
  });
  const [activeResultTab, setActiveResultTab] = useState("success");
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState(null);
  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const fileInputRef = useRef();
  const [addStockModalOpen, setAddStockModalOpen] = useState(false);
  const [addStockItem, setAddStockItem] = useState(null);
  const [addStockQuantity, setAddStockQuantity] = useState("");
  const [addStockReason, setAddStockReason] = useState("");
  const [removeStockModalOpen, setRemoveStockModalOpen] = useState(false);
  const [removeStockItem, setRemoveStockItem] = useState(null);
  const [removeStockQuantity, setRemoveStockQuantity] = useState("");
  const [removeStockReason, setRemoveStockReason] = useState("");
  const [adjustStockModalOpen, setAdjustStockModalOpen] = useState(false);
  const [adjustStockItem, setAdjustStockItem] = useState(null);
  const [adjustStockQuantity, setAdjustStockQuantity] = useState("");
  const [adjustStockReason, setAdjustStockReason] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkStockModalOpen, setBulkStockModalOpen] = useState(false);
  const [bulkStockQuantity, setBulkStockQuantity] = useState("");
  const [bulkStockReason, setBulkStockReason] = useState("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedItemForDetails, setSelectedItemForDetails] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [bulkInitModalOpen, setBulkInitModalOpen] = useState(false);
  const [bulkInitLoading, setBulkInitLoading] = useState(false);
  const [skusWithoutInventory, setSkusWithoutInventory] = useState(null);

  useEffect(() => {
    setLoading(true);
    console.log("🔄 Loading initial data...");

    Promise.all([getInventoryItems(), getAllProducts()])
      .then(([inv, prods]) => {
        console.log("📋 Loaded inventory items:", inv.length);
        console.log("📋 First few inventory items:", inv.slice(0, 3));
        console.log("📋 Loaded products:", prods.length);
        console.log("📋 First few products:", prods.slice(0, 3));

        setItems(inv);
        setFilteredItems(inv);
        setProducts(prods);

        console.log("✅ Initial data loading completed");
      })
      .catch((e) => {
        console.error("❌ Error loading data:", e);
        setError(e.message);
      })
      .finally(() => {
        setLoading(false);
        console.log("🏁 Loading state set to false");
      });
  }, []);

  // Search and filter effect
  useEffect(() => {
    let filtered = [...items];

    // Apply search term (search all fields)
    if (searchTerm) {
      filtered = filtered.filter((item) => {
        const term = searchTerm.toLowerCase();
        return (
          (item.productSku?.name || item.productSku?.product?.name)
            ?.toLowerCase()
            .includes(term) ||
          item.productSku?.product?.itemCode?.toLowerCase().includes(term) ||
          item.productSku?.sku?.toLowerCase().includes(term) ||
          item.productSku?.barcode?.toLowerCase().includes(term) ||
          item.location?.toLowerCase().includes(term) ||
          item.batch?.toLowerCase().includes(term) ||
          item.supplier?.name?.toLowerCase().includes(term) ||
          item.supplier?.code?.toLowerCase().includes(term)
        );
      });
    }

    // Apply location filter
    if (locationFilter) {
      filtered = filtered.filter((item) =>
        item.location?.toLowerCase().includes(locationFilter.toLowerCase()),
      );
    }

    // Apply supplier filter
    if (supplierFilter) {
      filtered = filtered.filter((item) =>
        item.supplier?.name
          ?.toLowerCase()
          .includes(supplierFilter.toLowerCase()),
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter((item) => {
        const now = new Date();
        const expiryDate = item.expiryDate ? new Date(item.expiryDate) : null;
        const isExpired = expiryDate && expiryDate < now;
        const isExpiringSoon =
          expiryDate &&
          !isExpired &&
          expiryDate <= new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        const isLowStock = item.minStock && item.quantity <= item.minStock;

        switch (statusFilter) {
          case "expired":
            return isExpired;
          case "expiring_soon":
            return isExpiringSoon;
          case "low_stock":
            return isLowStock;
          case "good":
            return !isExpired && !isExpiringSoon && !isLowStock;
          default:
            return true;
        }
      });
    }

    setFilteredItems(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [items, searchTerm, locationFilter, supplierFilter, statusFilter]);

  // Sorting Logic
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedItems = useMemo(() => {
    let sortableItems = [...filteredItems];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case "productName":
            aValue = a.productSku?.name || a.productSku?.product?.name || "";
            bValue = b.productSku?.name || b.productSku?.product?.name || "";
            break;
          case "supplier":
            aValue = a.supplier?.name || "";
            bValue = b.supplier?.name || "";
            break;
          case "location":
            aValue = a.location || "";
            bValue = b.location || "";
            break;
          case "batch":
            aValue = a.batch || "";
            bValue = b.batch || "";
            break;
          case "quantity":
            aValue = a.quantity || 0;
            bValue = b.quantity || 0;
            break;
          case "minStock":
            aValue = a.minStock || 0;
            bValue = b.minStock || 0;
            break;
          case "expiryDate":
            aValue = a.expiryDate ? new Date(a.expiryDate).getTime() : 0;
            bValue = b.expiryDate ? new Date(b.expiryDate).getTime() : 0;
            break;
          default:
            aValue = "";
            bValue = "";
        }

        // Handle string comparison
        if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredItems, sortConfig]);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setLocationFilter("");
    setSupplierFilter("");
    setStatusFilter("");
  };

  const hasActiveFilters =
    searchTerm || locationFilter || supplierFilter || statusFilter;

  // Stats calculation
  const stats = useMemo(() => {
    const now = new Date();
    const thirtyDaysFromNow = new Date(
      now.getTime() + 30 * 24 * 60 * 60 * 1000,
    );

    return {
      total: items.length,
      totalQuantity: items.reduce((sum, item) => sum + (item.quantity || 0), 0),
      lowStock: items.filter(
        (item) => item.minStock && item.quantity <= item.minStock,
      ).length,
      expiringSoon: items.filter((item) => {
        const expiryDate = item.expiryDate ? new Date(item.expiryDate) : null;
        return (
          expiryDate && expiryDate > now && expiryDate <= thirtyDaysFromNow
        );
      }).length,
    };
  }, [items]);

  // Get unique values for filter dropdowns
  const getUniqueLocations = () => {
    const locations = items.map((item) => item.location).filter(Boolean);
    return [...new Set(locations)].sort();
  };

  const getUniqueSuppliers = () => {
    const suppliers = items.map((item) => item.supplier?.name).filter(Boolean);
    return [...new Set(suppliers)].sort();
  };

  // Check if user has access to inventory (admin or sales executive)
  if (!isAdmin && !isSalesExecutive) {
    return (
      <div className="p-8 text-red-600 font-bold">
        Access denied: Admin or Sales Executive access required
      </div>
    );
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel"
      ) {
        setImportFile(file);
        setImportError(null);
      } else {
        setImportFile(null);
        setImportError(
          "Invalid file type. Please select an Excel file (.xlsx or .xls)",
        );
      }
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportError("Please select a file to import");
      return;
    }

    setImporting(true);
    setImportProgress({
      total: 0,
      current: 0,
      success: 0,
      created: 0,
      updated: 0,
      skipped: 0,
      errors: 0,
      errorDetails: [],
      successDetails: [],
    });

    try {
      // Parse the Excel file
      const data = await importFile.arrayBuffer();
      const workbook = XLSX.read(data, {
        cellDates: true,
        cellNF: false,
        cellText: false,
      });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      const allRows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
        dateNF: "yyyy-mm-dd",
      });

      const dataRows = allRows.slice(1);
      const columnMapping = [
        "Item Code*",
        "SKU Code*",
        "Barcode",
        "Item Name*",
        "Brand / Manufacturer",
        "HSN / Tariff Code",
        "Quantity Received*",
        "Unit of Measure (UOM)",
        "Pack Size",
        "Date Received",
        "Receiving Location",
        "Received By",
        "Supplier Delivery Note / GRN No.",
        "Our Purchase Order Number",
        "Supplier Code",
        "Supplier Name",
        "Supplier Invoice Number",
        "Supplier Invoice Date",
        "Unit Cost",
        "Currency",
        "Tax Rate (%)",
        "GST Amount",
        "Total Procurement Value",
        "Batch Number",
        "Manufacturing Date",
        "Expiry Date",
        "Stock Status",
        "Storage Conditions",
        "Bin / Rack Location",
        "Procurement Remarks",
        "Attachments",
      ];

      const rows = dataRows
        .filter((row) => {
          const hasValidData = row.some(
            (cell) =>
              cell &&
              typeof cell === "string" &&
              cell.trim() !== "" &&
              !/^\d+$/.test(cell.trim()),
          );
          return hasValidData;
        })
        .map((row) => {
          const obj = {};
          columnMapping.forEach((columnName, index) => {
            obj[columnName] = row[index] || "";
          });
          return obj;
        });

      if (rows.length === 0) {
        setImportError(
          "No data found in the Excel file. Please check the format.",
        );
        setImporting(false);
        return;
      }

      setImportProgress((prev) => ({
        ...prev,
        total: rows.length,
      }));

      // Fetch existing inventory items once before the loop
      const existingInventoryItems = await getInventoryItems();

      let created = 0,
        updated = 0,
        skipped = 0,
        errors = 0;
      const errorDetails = [];
      const successDetails = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const itemCode = row["Item Code*"] || row["Item Code"] || "";
        const skuCode = row["SKU Code*"] || row["SKU Code"] || "";
        const itemName = row["Item Name*"] || row["Item Name"] || "";

        setImportProgress((prev) => ({
          ...prev,
          current: i + 1,
        }));

        // Find product and SKU
        let productSku = null;
        let product = null;

        if (skuCode && skuCode.trim() !== "") {
          for (const p of products) {
            const foundSku = p.skus?.find((sku) => sku.sku === skuCode.trim());
            if (foundSku) {
              productSku = foundSku;
              product = p;
              break;
            }
          }
        }

        if (!product && itemCode && itemCode.trim() !== "") {
          product = products.find((p) => p.itemCode === itemCode);
          if (product && product.skus?.length > 0) {
            if (skuCode && skuCode.trim() !== "") {
              productSku = product.skus?.find(
                (sku) => sku.sku === skuCode.trim(),
              );
            }
            if (!productSku) {
              productSku = product.skus[0];
            }
          }
        }

        if (!product || !productSku) {
          errors++;
          errorDetails.push({
            row: i + 2,
            itemCode: itemCode || "N/A",
            skuCode: skuCode || "N/A",
            itemName: itemName || "N/A",
            error: !product
              ? "Product not found"
              : "SKU not found in product master",
          });
          continue;
        }

        try {
          // Find or create supplier
          let supplier = null;
          const supplierName = row["Supplier Name"];
          const supplierCode = row["Supplier Code"];

          if (supplierName && supplierName.trim() !== "") {
            const suppliers = await getSuppliers();
            supplier = suppliers.find((s) => s.name === supplierName);

            if (!supplier) {
              supplier = await createSupplier({
                name: supplierName,
                code: supplierCode || supplierName.substring(0, 10),
                contactPerson: "",
                email: "",
                phone: "",
              });
            }
          }

          // Check for existing inventory
          const existingInventory = existingInventoryItems.find(
            (inv) => inv.productSkuId === productSku.id,
          );

          const quantityReceived = row["Quantity Received*"];
          console.log(`📊 Processing quantity for SKU ${productSku.sku}:`, {
            raw: quantityReceived,
            type: typeof quantityReceived,
            parsed: parseFloat(quantityReceived),
          });

          const quantityValue = parseFloat(quantityReceived);
          if (
            quantityReceived === undefined ||
            quantityReceived === null ||
            quantityReceived === "" ||
            isNaN(quantityValue) ||
            quantityValue <= 0
          ) {
            errors++;
            errorDetails.push({
              row: i + 2,
              itemCode: product?.itemCode || row["Item Code*"],
              skuCode: productSku?.sku || row["SKU Code*"],
              itemName: product?.name || row["Item Name*"],
              error: `Please enter a valid quantity (current: "${quantityReceived}"). Must be a number greater than 0.`,
            });
            setImportProgress((prev) => ({
              ...prev,
              current: i + 1,
              errors: errors,
            }));
            continue;
          }

          const inventoryData = {
            productSkuId: productSku.id,
            quantity: quantityValue,
            locationCode: row["Bin / Rack Location"] || null,
            batchNumber: row["Batch Number"] || null,
            manufacturingDate: row["Manufacturing Date"] || null,
            expiryDate: row["Expiry Date"] || null,
            stockStatus: row["Stock Status"] || "In Stock",
            storageConditions: row["Storage Conditions"] || null,
            supplierId: supplier?.id || null,
            unitCost: parseFloat(row["Unit Cost"]) || 0,
          };

          console.log(
            `📋 Creating/updating inventory for SKU ${productSku.sku}:`,
            inventoryData,
          );

          if (existingInventory) {
            if (replaceExisting) {
              await updateInventoryItem(existingInventory.id, inventoryData);
              updated++;
              successDetails.push({
                row: i + 2,
                itemCode: product.itemCode,
                skuCode: productSku.sku,
                itemName: product.name,
                action: "Updated",
              });
            } else {
              skipped++;
              successDetails.push({
                row: i + 2,
                itemCode: product.itemCode,
                skuCode: productSku.sku,
                itemName: product.name,
                action: "Skipped (exists)",
              });
            }
          } else {
            await createInventoryItem(inventoryData);
            created++;
            successDetails.push({
              row: i + 2,
              itemCode: product.itemCode,
              skuCode: productSku.sku,
              itemName: product.name,
              action: "Created",
            });
          }
        } catch (error) {
          errors++;
          errorDetails.push({
            row: i + 2,
            itemCode: product.itemCode,
            skuCode: productSku.sku,
            itemName: product.name,
            error: error.message || "Failed to create/update inventory",
          });
        }
      }

      setImportProgress({
        total: rows.length,
        current: rows.length,
        success: created + updated,
        created,
        updated,
        skipped,
        errors,
        errorDetails,
        successDetails,
      });

      setActiveResultTab(errors > 0 ? "errors" : "success");

      // Refresh inventory list
      const inv = await getInventoryItems();
      setItems(inv);
      setFilteredItems(inv);
    } catch (error) {
      console.error("Import error:", error);
      setImportError(
        error.message ||
          "Import failed. Please check your file format and try again.",
      );
      setImportProgress({
        total: 0,
        current: 0,
        success: 0,
        created: 0,
        updated: 0,
        skipped: 0,
        errors: 1,
        errorDetails: [
          {
            row: 0,
            itemCode: "N/A",
            skuCode: "N/A",
            itemName: "N/A",
            error:
              error.message ||
              "Failed to parse Excel file. Please check the format.",
          },
        ],
        successDetails: [],
      });
      setActiveResultTab("errors");
    } finally {
      setImporting(false);
    }
  };

  // Helper function to parse Excel date
  const parseExcelDate = (dateValue) => {
    if (!dateValue) return null;

    // If it's already a Date object
    if (dateValue instanceof Date) {
      return dateValue;
    }

    // If it's a number (Excel serial date)
    if (typeof dateValue === "number") {
      // Check if it's a reasonable Excel serial date (not in the future or too far in past)
      if (dateValue < 1 || dateValue > 100000) {
        return null; // Invalid Excel serial date
      }
      // Excel dates are days since 1900-01-01
      const excelEpoch = new Date(1900, 0, 1);
      const date = new Date(
        excelEpoch.getTime() + (dateValue - 1) * 24 * 60 * 60 * 1000,
      );
      // Validate the resulting date
      if (date.getFullYear() < 1900 || date.getFullYear() > 2100) {
        return null; // Invalid year
      }
      return date;
    }

    // If it's a string, try various formats
    if (typeof dateValue === "string") {
      const trimmedValue = dateValue.trim();

      // Skip obviously invalid dates
      if (
        trimmedValue.includes("+04") ||
        trimmedValue.includes("+05") ||
        trimmedValue.includes("+06")
      ) {
        return null;
      }

      // Common Excel date formats
      const dateFormats = [
        // YYYY-MM-DD (ISO format - most reliable)
        /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
        // DD/MM/YYYY (European format)
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
        // DD-MM-YYYY (European format)
        /^(\d{1,2})-(\d{1,2})-(\d{4})$/,
        // DD/MM/YY (European format)
        /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/,
        // DD-MM-YY (European format)
        /^(\d{1,2})-(\d{1,2})-(\d{2})$/,
        // MM/DD/YYYY (US format)
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
        // MM-DD-YYYY (US format)
        /^(\d{1,2})-(\d{1,2})-(\d{4})$/,
        // DD.MM.YYYY (German format)
        /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/,
        // DD.MM.YY (German format)
        /^(\d{1,2})\.(\d{1,2})\.(\d{2})$/,
      ];

      for (let i = 0; i < dateFormats.length; i++) {
        const format = dateFormats[i];
        const match = trimmedValue.match(format);
        if (match) {
          let day, month, year;

          // Handle different formats based on position in array
          if (i === 0) {
            // YYYY-MM-DD format
            year = parseInt(match[1]);
            month = parseInt(match[2]) - 1; // Month is 0-indexed
            day = parseInt(match[3]);
          } else if (i >= 1 && i <= 4) {
            // European formats (DD/MM/YYYY, DD-MM-YYYY, DD/MM/YY, DD-MM-YY)
            day = parseInt(match[1]);
            month = parseInt(match[2]) - 1; // Month is 0-indexed
            year = parseInt(match[3]);
            if (year < 100) year += 2000; // Convert 2-digit year to 4-digit
          } else if (i >= 5 && i <= 6) {
            // US formats (MM/DD/YYYY, MM-DD-YYYY)
            month = parseInt(match[1]) - 1; // Month is 0-indexed
            day = parseInt(match[2]);
            year = parseInt(match[3]);
          } else {
            // German formats (DD.MM.YYYY, DD.MM.YY)
            day = parseInt(match[1]);
            month = parseInt(match[2]) - 1; // Month is 0-indexed
            year = parseInt(match[3]);
            if (year < 100) year += 2000; // Convert 2-digit year to 4-digit
          }

          const date = new Date(year, month, day);

          // Validate the date
          if (
            date.getFullYear() === year &&
            date.getMonth() === month &&
            date.getDate() === day &&
            date.getFullYear() > 1900 &&
            date.getFullYear() < 2100
          ) {
            return date;
          }
        }
      }

      // Try ISO string format as fallback
      const parsed = new Date(trimmedValue);
      if (
        !isNaN(parsed.getTime()) &&
        parsed.getTime() > 0 &&
        parsed.getFullYear() > 1900 &&
        parsed.getFullYear() < 2100
      ) {
        return parsed;
      }
    }

    return null;
  };

  // Bulk Initialize handlers
  const handleOpenBulkInit = async () => {
    setBulkInitModalOpen(true);
    setBulkInitLoading(true);
    try {
      const data = await getSkusWithoutInventory();
      setSkusWithoutInventory(data);
    } catch (err) {
      console.error("Error fetching SKUs without inventory:", err);
      setToast({ type: "error", message: "Failed to load SKU data" });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setBulkInitLoading(false);
    }
  };

  const handleBulkInitialize = async () => {
    setBulkInitLoading(true);
    try {
      const result = await bulkInitializeInventory();
      setToast({
        type: "success",
        message: `Successfully initialized ${result.created} inventory entries!`,
      });
      setTimeout(() => setToast(null), 3000);
      setBulkInitModalOpen(false);
      // Refresh inventory data
      const inv = await getInventoryItems();
      setItems(inv);
      setFilteredItems(inv);
    } catch (err) {
      console.error("Error bulk initializing inventory:", err);
      setToast({
        type: "error",
        message: err.message || "Failed to initialize inventory",
      });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setBulkInitLoading(false);
    }
  };

  const handleImportAll = async () => {
    console.log("🚀 Import All button clicked!");
    console.log("📋 Imported rows available:", importedRows.length);
    console.log("📋 Products available:", products.length);

    if (!importedRows || importedRows.length === 0) {
      console.error("❌ No imported rows available");
      setImportError("No data to import. Please upload an Excel file first.");
      return;
    }

    if (!products || products.length === 0) {
      console.error("❌ No products loaded");
      setImportError(
        "Products not loaded. Please refresh the page and try again.",
      );
      return;
    }

    setImporting(true);
    setImportError(null);
    let success = 0,
      fail = 0;
    const skippedItems = []; // Track items that were skipped due to missing SKU codes

    console.log("🔄 Starting import process...");

    for (const row of importedRows) {
      // Extract Item Code, SKU Code, and Item Name from the row with correct column names
      const itemCode = row["Item Code*"] || row["Item Code"] || "";
      const skuCode = row["SKU Code*"] || row["SKU Code"] || row["SKU"] || "";
      const barcode = row["Barcode"] || "";
      const itemName = row["Item Name*"] || row["Item Name"] || "";

      console.log(`🔍 Row ${success + fail + 1}:`);
      console.log(`   Raw row data:`, row);
      console.log(`   Extracted Item Code: "${itemCode}"`);
      console.log(`   Available products: ${products.length}`);
      console.log(
        `   First 3 product codes:`,
        products.slice(0, 3).map((p) => p.itemCode),
      );

      // Find product and SKU using enhanced lookup logic
      let productSku = null;
      let product = null;
      let lookupMethod = "";

      // Method 1: Try to find by SKU code directly (preferred method)
      if (skuCode && skuCode.trim() !== "") {
        for (const p of products) {
          const foundSku = p.skus?.find((sku) => sku.sku === skuCode.trim());
          if (foundSku) {
            productSku = foundSku;
            product = p;
            lookupMethod = "SKU Code";
            break;
          }
        }
      }

      // Method 2: Try to find by barcode
      if (!productSku && barcode && barcode.trim() !== "") {
        for (const p of products) {
          const foundSku = p.skus?.find(
            (sku) => sku.barcode === barcode.trim(),
          );
          if (foundSku) {
            productSku = foundSku;
            product = p;
            lookupMethod = "Barcode";
            break;
          }
        }
      }

      // Method 3: Try to find by product item code first, then find SKU
      if (!product && itemCode && itemCode.trim() !== "") {
        product = products.find((p) => p.itemCode === itemCode);
        if (product) {
          console.log(
            `🔍 Found product by item code: ${product.name} (${product.itemCode})`,
          );
          console.log(
            `📋 Available SKUs:`,
            product.skus?.map((s) => `${s.sku} - ${s.name}`).join(", ") ||
              "None",
          );

          // If we have a specific SKU code, try to find it
          if (skuCode && skuCode.trim() !== "") {
            productSku = product.skus?.find(
              (sku) => sku.sku === skuCode.trim(),
            );
            if (productSku) {
              lookupMethod = "Product Item Code + SKU Code";
              console.log(
                `✅ Exact SKU match found: ${productSku.sku} - ${productSku.name}`,
              );
            } else {
              console.log(
                `⚠️ SKU "${skuCode}" not found for product "${product.name}"`,
              );
              console.log(
                `   Available SKUs for ${product.name}:`,
                product.skus?.map((s) => s.sku).join(", ") || "None",
              );
            }
          }

          // If no specific SKU found, try to match by item name (only if no SKU code was provided)
          if (!productSku && product.skus?.length > 0) {
            if (
              itemName &&
              itemName.trim() !== "" &&
              (!skuCode || skuCode.trim() === "")
            ) {
              // Try to find SKU by matching the item name (only when no SKU code provided)
              productSku = product.skus?.find(
                (sku) =>
                  sku.name.toLowerCase().includes(itemName.toLowerCase()) ||
                  itemName.toLowerCase().includes(sku.name.toLowerCase()),
              );
              if (productSku) {
                lookupMethod = "Product Item Code + Item Name Match";
                console.log(
                  `✅ Matched SKU by item name: ${productSku.sku} - ${productSku.name}`,
                );
              }
            }

            // If still no match and no SKU code was provided, use first available SKU
            if (!productSku && (!skuCode || skuCode.trim() === "")) {
              productSku = product.skus[0];
              lookupMethod = "Product Item Code + First SKU";
              console.log(
                `⚠️ No specific SKU code provided, using first available SKU: ${productSku.sku} - ${productSku.name}`,
              );
            }
          }
        }
      }

      if (!product || !productSku) {
        console.error(`❌ Product/SKU not found:`);
        console.error(`   Item Code: "${itemCode}"`);
        console.error(`   SKU Code: "${skuCode}"`);
        console.error(`   Barcode: "${barcode}"`);
        console.error(`   Item Name: "${itemName}"`);
        console.error(
          `   Available Item Codes:`,
          products.map((p) => p.itemCode).join(", "),
        );
        console.error(
          `   Available SKUs:`,
          products
            .flatMap((p) => p.skus?.map((sku) => sku.sku) || [])
            .join(", "),
        );
        console.error(
          `   Available Barcodes:`,
          products
            .flatMap((p) => p.skus?.map((sku) => sku.barcode) || [])
            .join(", "),
        );

        // Track skipped items for user feedback
        skippedItems.push({
          row: success + fail + 1,
          itemCode: itemCode,
          skuCode: skuCode,
          itemName: itemName,
          reason: !product
            ? "Product not found"
            : "SKU code not found in product master",
        });

        fail++;
        continue;
      }

      console.log(
        `✅ Product found: ${product.name} (${product.itemCode}) via ${lookupMethod}`,
      );
      console.log(
        `✅ SKU found: ${productSku.sku} (${
          productSku.barcode || "No barcode"
        })`,
      );

      console.log(`✅ Product found: ${product.name} (${product.itemCode})`);

      // Category and Subcategory information is available from product master
      // product.primaryCategory?.name - for category
      // product.primaryCategory?.parent?.name - for subcategory (if applicable)

      try {
        // Create or find supplier
        let supplier = null;
        let supplierInvoice = null;
        let purchaseOrder = null;

        const supplierName = row["Supplier Name"];
        const supplierCode = row["Supplier Code"];

        if (supplierName && supplierName.trim() !== "") {
          // First, try to find existing supplier by code (if provided)
          const code =
            supplierCode && supplierCode.trim() !== ""
              ? supplierCode.trim().toUpperCase()
              : supplierName
                  .trim()
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, "")
                  .substring(0, 10);

          console.log("🔍 Looking for existing supplier:", {
            supplierName: supplierName.trim(),
            supplierCode: code,
          });

          // First try to find by supplier code (most reliable)
          if (supplierCode && supplierCode.trim() !== "") {
            try {
              const allSuppliers = await getSuppliers();
              supplier = allSuppliers.find((s) => s.code === code);
              if (supplier) {
                console.log(
                  `✅ Found existing supplier by code: ${supplier.name} (${supplier.code})`,
                );
              } else {
                console.log(`❌ No supplier found with code: ${code}`);
              }
            } catch (fetchError) {
              console.error(
                "Failed to fetch suppliers for code lookup:",
                fetchError,
              );
            }
          }

          // If not found by code, try to find by name
          if (!supplier) {
            try {
              supplier = await getSupplierByName(supplierName.trim());
              if (supplier) {
                console.log(
                  `✅ Found existing supplier by name: ${supplier.name} (${supplier.code})`,
                );
              } else {
                console.log(`❌ No supplier found with name: ${supplierName}`);
              }
            } catch (fetchError) {
              console.error(
                "Failed to fetch existing suppliers by name:",
                fetchError,
              );
            }
          }

          // Only create new supplier if none exists
          if (!supplier) {
            console.log("🆕 Creating new supplier:", {
              name: supplierName.trim(),
              code: code,
            });
            try {
              supplier = await createSupplier({
                name: supplierName.trim(),
                code: code,
              });
              console.log(
                `✅ New supplier created: ${supplier.name} (${supplier.code})`,
              );
            } catch (e) {
              console.error("❌ Failed to create new supplier:", {
                error: e.message,
                supplierName: supplierName.trim(),
                supplierCode: code,
              });
            }
          }
        }

        // Create purchase order if specified
        const poNumber = row["Our Purchase Order Number"];
        if (supplier && poNumber && poNumber.trim() !== "") {
          try {
            purchaseOrder = await createPurchaseOrder({
              orderNumber: poNumber.trim(),
              supplierId: supplier.id,
              orderDate: new Date(),
              status: "COMPLETED",
            });
          } catch (e) {
            console.log("Purchase order might exist, continuing...");
          }
        }

        // Create supplier invoice if specified
        const invoiceNumber = row["Supplier Invoice Number"];
        const invoiceDate = row["Supplier Invoice Date"];
        const procurementValue = row["Total Procurement Value"];

        if (supplier && invoiceNumber && invoiceNumber.trim() !== "") {
          try {
            console.log("🔧 Creating supplier invoice with data:", {
              invoiceNumber: invoiceNumber.trim(),
              supplierId: supplier.id,
              invoiceDate: invoiceDate,
              procurementValue: procurementValue,
            });

            const invoiceDateParsed = parseExcelDate(invoiceDate);
            console.log("📅 Invoice date parsing:", {
              original: invoiceDate,
              parsed: invoiceDateParsed,
              type: typeof invoiceDate,
            });

            const totalAmount = procurementValue
              ? parseFloat(procurementValue)
              : 0;
            console.log("💰 Amount parsing:", {
              original: procurementValue,
              parsed: totalAmount,
            });

            const invoiceData = {
              invoiceNumber: invoiceNumber.trim(),
              supplierId: supplier.id,
              purchaseOrderId: purchaseOrder?.id || null,
              invoiceDate: invoiceDateParsed || new Date(),
              totalAmount: totalAmount,
              balanceAmount: totalAmount, // Initially unpaid
              currency: row["Currency"] || "INR",
              taxRate: row["Tax Rate (%)"]
                ? parseFloat(row["Tax Rate (%)"])
                : null,
              status: "PENDING",
              grnNumber: row["Supplier Delivery Note / GRN No."],
              deliveryNote: row["Supplier Delivery Note / GRN No."],
              notes: row["Procurement Remarks"],
            };

            console.log("📋 Final invoice data:", invoiceData);

            supplierInvoice = await createSupplierInvoice(invoiceData);
            console.log(
              "✅ Supplier invoice created successfully:",
              supplierInvoice.invoiceNumber,
            );
          } catch (e) {
            console.error("❌ Failed to create supplier invoice:", {
              error: e.message,
              invoiceNumber: invoiceNumber.trim(),
              supplierId: supplier.id,
              invoiceDate: invoiceDate,
            });
          }
        }

        // Calculate total value if unit cost and quantity are available
        const unitCost =
          row["Unit Cost"] && row["Unit Cost"] !== "N/A"
            ? Number(row["Unit Cost"])
            : null;
        const quantity = parseInt(
          row["Quantity Received*"] ||
            row["Quantity Received"] ||
            row["Quantity"] ||
            "0",
        );
        const totalValue = unitCost && quantity ? unitCost * quantity : null;

        console.log("📊 Quantity and cost data:", {
          rawQuantity:
            row["Quantity Received*"] ||
            row["Quantity Received"] ||
            row["Quantity"],
          parsedQuantity: quantity,
          rawUnitCost: row["Unit Cost"],
          parsedUnitCost: unitCost,
          totalValue: totalValue,
        });

        // Enhanced product master data extraction and logging
        console.log("📋 Product Master Data:", {
          productName: product.name,
          productCode: product.itemCode,
          brand: product.brand,
          hsCode: product.hsCode,
          unitOfMeasure: product.unitOfMeasure,
          packSize: product.packSize,
          category: product.category,
          description: product.description,
          costPrice: product.costPrice,
          sellingPrice: product.sellingPrice,
          gstPercent: product.gstPercent,
        });

        console.log("📋 SKU Master Data:", {
          skuCode: productSku.sku,
          skuName: productSku.name,
          skuDescription: productSku.description,
          skuBarcode: productSku.barcode,
          skuCostPrice: productSku.costPrice,
          skuSellingPrice: productSku.sellingPrice,
          skuWeight: productSku.weight,
          skuDimensions: productSku.dimensions,
        });

        // Prepare comprehensive notes with enhanced product master information
        const additionalNotes = [];

        // Product Master Information (Priority)
        additionalNotes.push(`Product: ${product.name} (${product.itemCode})`);
        additionalNotes.push(
          `SKU: ${productSku.sku} - ${productSku.name || "N/A"}`,
        );
        if (productSku.barcode) {
          additionalNotes.push(`Barcode: ${productSku.barcode}`);
        }

        // Brand and Technical Information (from Product Master, fallback to import data)
        const brand = product.brand || row["Brand / Manufacturer"] || "N/A";
        additionalNotes.push(`Brand: ${brand}`);

        const hsCode = product.hsCode || row["HSN / Tariff Code"] || "N/A";
        additionalNotes.push(`HSN: ${hsCode}`);

        const packSize = product.packSize || row["Pack Size"] || "N/A";
        additionalNotes.push(`Pack Size: ${packSize}`);

        const uom =
          product.unitOfMeasure || row["Unit of Measure (UOM)"] || "N/A";
        additionalNotes.push(`UOM: ${uom}`);

        // Pricing Information
        if (row["Currency"]) {
          additionalNotes.push(`Currency: ${row["Currency"]}`);
        }

        const taxRate = row["Tax Rate (%)"] || product.gstPercent || "N/A";
        additionalNotes.push(`Tax Rate: ${taxRate}%`);

        if (row["Total Procurement Value"]) {
          additionalNotes.push(
            `Total Value: ${row["Total Procurement Value"]}`,
          );
        }

        // Supplier Information
        if (supplier) {
          additionalNotes.push(`Supplier: ${supplier.name} (${supplier.code})`);
        }

        // Additional Information
        if (row["Procurement Remarks"]) {
          additionalNotes.push(`Remarks: ${row["Procurement Remarks"]}`);
        }

        if (row["Attachments"]) {
          additionalNotes.push(`Attachments: ${row["Attachments"]}`);
        }

        const combinedNotes = additionalNotes.join(" | ");

        // Convert Date objects to ISO strings for backend
        const expiryDate = parseExcelDate(row["Expiry Date"]);
        const manufacturingDate = parseExcelDate(row["Manufacturing Date"]);
        const dateReceived = parseExcelDate(row["Date Received"]);

        console.log("📅 Date parsing results:", {
          rawExpiryDate: row["Expiry Date"],
          parsedExpiryDate: expiryDate,
          rawManufacturingDate: row["Manufacturing Date"],
          parsedManufacturingDate: manufacturingDate,
          rawDateReceived: row["Date Received"],
          parsedDateReceived: dateReceived,
        });

        // Enhanced inventory data with product master integration
        const inventoryData = {
          productSkuId: productSku.id,
          supplierId: supplier?.id || null,
          purchaseOrderId: purchaseOrder?.id || null,
          supplierInvoiceId: supplierInvoice?.id || null,
          location: row["Receiving Location"] || row["Location"],
          batch: row["Batch Number"] || row["Batch"],
          quantity: Math.max(0, quantity || 0),
          minStock: null, // Not in new template
          expiryDate: expiryDate ? expiryDate.toISOString() : null,
          locationCode: row["Bin / Rack Location"] || row["Location Code"],
          storageConditions: row["Storage Conditions"],
          batchNumber: row["Batch Number"] || row["Batch"],
          manufacturingDate: manufacturingDate
            ? manufacturingDate.toISOString()
            : null,
          supplierBatch: row["Batch Number"] || row["Batch"], // Using batch number as supplier batch
          qualityStatus: row["Stock Status"] || "Available",
          inspectorName: row["Received By"],
          notes: combinedNotes,

          // Enhanced pricing with product master fallback
          unitCost:
            unitCost || productSku.costPrice || product.costPrice || null,
          totalValue:
            totalValue || (unitCost && quantity ? unitCost * quantity : null),

          dateReceived: dateReceived ? dateReceived.toISOString() : null,
          receivedBy: row["Received By"],
          grnNumber: row["Supplier Delivery Note / GRN No."],

          // Enhanced currency and tax with product master fallback
          currency: row["Currency"] || "INR",
          taxRate: row["Tax Rate (%)"]
            ? parseFloat(row["Tax Rate (%)"])
            : product.gstPercent || null,
          gstAmount: row["GST Amount"] ? parseFloat(row["GST Amount"]) : null, // Required for supplier-wise input tax reporting

          stockStatus: row["Stock Status"] || "Available",
          attachments: row["Attachments"],

          // Add supplier information to ensure proper linking
          supplierName: supplier?.name || null,
          supplierCode: supplier?.code || null,
        };

        console.log(
          "📦 Creating inventory item with enhanced product master data:",
          {
            productSkuId: inventoryData.productSkuId,
            productName: product.name,
            skuCode: productSku.sku,
            skuName: productSku.name,
            quantity: inventoryData.quantity,
            unitCost: inventoryData.unitCost,
            totalValue: inventoryData.totalValue,
            taxRate: inventoryData.taxRate,
            currency: inventoryData.currency,
            location: inventoryData.location,
            supplier: supplier ? `${supplier.name} (${supplier.code})` : "None",
          },
        );

        await createInventoryItem(inventoryData);
        console.log(`✅ Successfully imported row ${success + fail + 1}:`, {
          productSku: productSku.sku,
          productName: productSku.product.name,
          quantity: quantity,
          location: row["Receiving Location"] || row["Location"],
        });
        success++;
      } catch (e) {
        console.error(`❌ Failed to import row ${success + fail + 1}:`, {
          row: row,
          error: e.message,
          stack: e.stack,
        });
        fail++;
      }
    }
    setImporting(false);

    // Show detailed import results with skipped items information
    let resultMessage = `Import completed: ${success} successful`;

    if (fail > 0) {
      resultMessage += `, ${fail} failed`;
    }

    if (skippedItems.length > 0) {
      resultMessage += `, ${skippedItems.length} skipped (SKU code not found in product master)`;

      // Log detailed information about skipped items
      console.log("⚠️ Skipped items due to missing SKU codes:", skippedItems);

      // Create detailed message for skipped items
      const skippedDetails = skippedItems
        .map(
          (item) =>
            `Row ${item.row}: ${item.itemCode} - ${item.skuCode} (${item.reason})`,
        )
        .join("\n");

      console.log("📋 Skipped items details:\n", skippedDetails);
    }

    if (success > 0) {
      setToast(
        `✅ Successfully imported ${success} items${
          fail > 0 ? `, ${fail} failed` : ""
        }${skippedItems.length > 0 ? `, ${skippedItems.length} skipped` : ""}`,
      );
      setTimeout(() => setToast(null), 5000); // Longer display time for more information
    }

    if (fail > 0 || skippedItems.length > 0) {
      setImportError(resultMessage + ". Check console for details.");
    } else {
      setImportError(null);
    }

    setImportedRows([]);
    // Clear the file input so the same file can be imported again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Refresh inventory data with better error handling
    try {
      const refreshedItems = await getInventoryItems();
      console.log(
        "📋 Refreshed inventory items after import:",
        refreshedItems.length,
      );
      console.log("📋 First few refreshed items:", refreshedItems.slice(0, 3));

      if (refreshedItems.length > 0) {
        setItems(refreshedItems);
        setFilteredItems(refreshedItems);
        console.log("✅ Inventory data refreshed successfully");
      } else {
        console.warn("⚠️ No inventory items found after refresh");
      }
    } catch (e) {
      console.error("❌ Error refreshing inventory after import:", e);
      setImportError(
        `Import completed but failed to refresh data: ${e.message}`,
      );
    }
  };

  const handleAdd = () => {
    navigate("/admin/inventory/add");
  };
  const handleEdit = (item) => {
    navigate(`/admin/inventory/edit/${item.id}`);
  };
  const handleSave = async (data) => {
    try {
      console.log("Saving inventory item data:", data);

      if (editItem) {
        console.log("Updating item with ID:", editItem.id);
        await updateInventoryItem(editItem.id, data);
        setToast("Inventory item updated");
      } else {
        console.log("Creating new item");
        await createInventoryItem(data);
        setToast("Inventory item added");
      }
      setModalOpen(false);
      setEditItem(null);
      setTimeout(() => setToast(null), 2000);
      getInventoryItems().then((inv) => {
        setItems(inv);
        setFilteredItems(inv);
      });
    } catch (e) {
      console.error("Error saving inventory item:", e);
      setToast("Error: " + e.message);
    }
  };

  const handleViewMovements = (item) => {
    setSelectedItem(item);
    setMovementModalOpen(true);
  };

  const handleViewDetails = (item) => {
    setSelectedItemForDetails(item);
    setDetailsModalOpen(true);
  };

  const handleEditFromDetails = (item) => {
    setDetailsModalOpen(false);
    navigate(`/admin/inventory/edit/${item.id}`);
  };

  const handleMovementAdded = () => {
    setToast("Stock movement added successfully");
    setTimeout(() => setToast(null), 2000);
    getInventoryItems().then(setItems);
  };

  const handleAddStock = (item) => {
    setAddStockItem(item);
    setAddStockQuantity("");
    setAddStockReason("");
    setAddStockModalOpen(true);
  };

  const handleAddStockSubmit = async (e) => {
    e.preventDefault();
    if (!addStockQuantity || !addStockReason) {
      setToast("Quantity and reason are required");
      return;
    }

    try {
      await addStockMovement(addStockItem.id, {
        type: "in",
        quantity: Number(addStockQuantity),
        reason: addStockReason,
        userId: null,
      });

      setToast("Stock added successfully");
      setAddStockModalOpen(false);
      setAddStockItem(null);
      setAddStockQuantity("");
      setAddStockReason("");
      setTimeout(() => setToast(null), 2000);
      getInventoryItems().then((inv) => {
        setItems(inv);
        setFilteredItems(inv);
      });
    } catch (e) {
      setToast("Error: " + e.message);
    }
  };

  const handleRemoveStock = (item) => {
    setRemoveStockItem(item);
    setRemoveStockQuantity("");
    setRemoveStockReason("");
    setRemoveStockModalOpen(true);
  };

  const handleRemoveStockSubmit = async (e) => {
    e.preventDefault();
    if (!removeStockQuantity || !removeStockReason) {
      setToast("Quantity and reason are required");
      return;
    }

    if (Number(removeStockQuantity) > removeStockItem.quantity) {
      setToast("Cannot remove more stock than available");
      return;
    }

    try {
      await addStockMovement(removeStockItem.id, {
        type: "out",
        quantity: Number(removeStockQuantity),
        reason: removeStockReason,
        userId: null,
      });

      setToast("Stock removed successfully");
      setRemoveStockModalOpen(false);
      setRemoveStockItem(null);
      setRemoveStockQuantity("");
      setRemoveStockReason("");
      setTimeout(() => setToast(null), 2000);
      getInventoryItems().then((inv) => {
        setItems(inv);
        setFilteredItems(inv);
      });
    } catch (e) {
      setToast("Error: " + e.message);
    }
  };

  const handleAdjustStock = (item) => {
    setAdjustStockItem(item);
    setAdjustStockQuantity(item.quantity.toString());
    setAdjustStockReason("");
    setAdjustStockModalOpen(true);
  };

  const handleAdjustStockSubmit = async (e) => {
    e.preventDefault();
    if (!adjustStockQuantity || !adjustStockReason) {
      setToast("Quantity and reason are required");
      return;
    }

    if (Number(adjustStockQuantity) < 0) {
      setToast("Quantity cannot be negative");
      return;
    }

    try {
      await addStockMovement(adjustStockItem.id, {
        type: "adjustment",
        quantity: Number(adjustStockQuantity),
        reason: adjustStockReason,
        userId: null,
      });

      setToast("Stock adjusted successfully");
      setAdjustStockModalOpen(false);
      setAdjustStockItem(null);
      setAdjustStockQuantity("");
      setAdjustStockReason("");
      setTimeout(() => setToast(null), 2000);
      getInventoryItems().then((inv) => {
        setItems(inv);
        setFilteredItems(inv);
      });
    } catch (e) {
      setToast("Error: " + e.message);
    }
  };

  const handleItemSelection = (itemId, checked) => {
    if (checked) {
      setSelectedItems((prev) => [...prev, itemId]);
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId));
    }
  };

  const handleBulkAddStock = () => {
    if (selectedItems.length === 0) {
      setToast("Please select items first");
      return;
    }
    setBulkStockQuantity("");
    setBulkStockReason("");
    setBulkStockModalOpen(true);
  };

  const handleBulkAddStockSubmit = async (e) => {
    e.preventDefault();
    if (!bulkStockQuantity || !bulkStockReason) {
      setToast("Quantity and reason are required");
      return;
    }

    try {
      const promises = selectedItems.map((itemId) =>
        addStockMovement(itemId, {
          type: "in",
          quantity: Number(bulkStockQuantity),
          reason: bulkStockReason,
          userId: null,
        }),
      );

      await Promise.all(promises);

      setToast(`Stock added to ${selectedItems.length} items successfully`);
      setBulkStockModalOpen(false);
      setSelectedItems([]);
      setBulkStockQuantity("");
      setBulkStockReason("");
      setTimeout(() => setToast(null), 2000);
      getInventoryItems().then((inv) => {
        setItems(inv);
        setFilteredItems(inv);
      });
    } catch (e) {
      setToast("Error: " + e.message);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {toast && (
        <div
          className={`fixed top-4 right-4 px-4 py-2 rounded shadow-lg z-50 ${
            typeof toast === "object" && toast.type === "error"
              ? "bg-red-600 text-white"
              : "bg-green-600 text-white"
          }`}
        >
          {typeof toast === "object" ? toast.message : toast}
        </div>
      )}
      <InventoryItemForm
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditItem(null);
        }}
        onSave={handleSave}
        initialData={editItem}
        products={products}
      />
      <InventoryMovementLog
        open={movementModalOpen}
        onClose={() => {
          setMovementModalOpen(false);
          setSelectedItem(null);
        }}
        inventoryItem={selectedItem}
        onMovementAdded={handleMovementAdded}
      />
      <InventoryDetailsModal
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedItemForDetails(null);
        }}
        item={selectedItemForDetails}
        onEdit={handleEditFromDetails}
      />

      {/* Add Stock Modal */}
      {addStockModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setAddStockModalOpen(false)}
            ></div>

            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Add Stock
                </h2>
                <button
                  onClick={() => setAddStockModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddStockSubmit} className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product
                  </label>
                  <div className="text-gray-900 font-medium">
                    {addStockItem?.product?.name}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Stock
                  </label>
                  <div className="text-gray-900 font-medium">
                    {addStockItem?.quantity || 0}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity to Add *
                  </label>
                  <input
                    type="number"
                    value={addStockQuantity || ""}
                    onChange={(e) => setAddStockQuantity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter quantity"
                    min="1"
                    step="1"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason *
                  </label>
                  <input
                    type="text"
                    value={addStockReason || ""}
                    onChange={(e) => setAddStockReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Purchase, Return, Adjustment"
                    required
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setAddStockModalOpen(false)}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Add Stock
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Remove Stock Modal */}
      {removeStockModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setRemoveStockModalOpen(false)}
            ></div>

            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Remove Stock
                </h2>
                <button
                  onClick={() => setRemoveStockModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              </div>

              <form onSubmit={handleRemoveStockSubmit} className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product
                  </label>
                  <div className="text-gray-900 font-medium">
                    {removeStockItem?.product?.name}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Stock
                  </label>
                  <div className="text-gray-900 font-medium">
                    {removeStockItem?.quantity || 0}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity to Remove *
                  </label>
                  <input
                    type="number"
                    value={removeStockQuantity || ""}
                    onChange={(e) => setRemoveStockQuantity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter quantity"
                    min="1"
                    max={removeStockItem?.quantity || 0}
                    step="1"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason *
                  </label>
                  <input
                    type="text"
                    value={removeStockReason || ""}
                    onChange={(e) => setRemoveStockReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Sale, Damaged, Expired"
                    required
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setRemoveStockModalOpen(false)}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Remove Stock
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Stock Modal */}
      {adjustStockModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setAdjustStockModalOpen(false)}
            ></div>

            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Adjust Stock
                </h2>
                <button
                  onClick={() => setAdjustStockModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAdjustStockSubmit} className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product
                  </label>
                  <div className="text-gray-900 font-medium">
                    {adjustStockItem?.product?.name}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Stock
                  </label>
                  <div className="text-gray-900 font-medium">
                    {adjustStockItem?.quantity || 0}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Stock Level *
                  </label>
                  <input
                    type="number"
                    value={adjustStockQuantity || ""}
                    onChange={(e) => setAdjustStockQuantity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter new quantity"
                    min="0"
                    step="1"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason *
                  </label>
                  <input
                    type="text"
                    value={adjustStockReason || ""}
                    onChange={(e) => setAdjustStockReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Correction, Audit, etc."
                    required
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setAdjustStockModalOpen(false)}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    Adjust Stock
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Stock Modal */}
      {bulkStockModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setBulkStockModalOpen(false)}
            ></div>

            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Bulk Add Stock
                </h2>
                <button
                  onClick={() => setBulkStockModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              </div>

              <form onSubmit={handleBulkAddStockSubmit} className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selected Items
                  </label>
                  <div className="text-gray-900 font-medium">
                    {selectedItems.length} items selected
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity to Add *
                  </label>
                  <input
                    type="number"
                    value={bulkStockQuantity || ""}
                    onChange={(e) => setBulkStockQuantity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter quantity"
                    min="1"
                    step="1"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason *
                  </label>
                  <input
                    type="text"
                    value={bulkStockReason || ""}
                    onChange={(e) => setBulkStockReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Bulk Purchase, Restock"
                    required
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setBulkStockModalOpen(false)}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Add Stock to All
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Inventory
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your inventory items and stock levels
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => exportToPDF(filteredItems)}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Export PDF
            </button>
            <button
              onClick={() => setImportModalOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>
            <button
              onClick={handleOpenBulkInit}
              className="inline-flex items-center gap-2 px-3 py-2 border border-green-300 dark:border-green-600 text-green-700 dark:text-green-300 text-sm font-medium rounded-xl hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
            >
              <Boxes className="w-4 h-4" />
              Auto Inventory
            </button>
            <button
              onClick={() => navigate("/admin/inventory/add")}
              disabled={products.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Add Inventory
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <Boxes className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.total}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Items
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <Package className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalQuantity.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Quantity
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.lowStock}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Low Stock
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.expiringSoon}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Expiring Soon
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Import Excel Modal */}
      {importModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                if (!importing) {
                  setImportModalOpen(false);
                  setImportFile(null);
                  setImportError(null);
                }
              }}
            ></div>

            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Import Inventory
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Follow the steps below to import your inventory data
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (!importing) {
                      setImportModalOpen(false);
                      setImportFile(null);
                      setImportError(null);
                    }
                  }}
                  disabled={importing}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Step 1: Download Template */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-sm">
                      1
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Download Template
                    </h3>
                  </div>
                  <div className="ml-11 space-y-3">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Download the Excel template with the correct format and
                      column headers
                    </p>
                    <button
                      onClick={downloadTemplate}
                      disabled={importing}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4" />
                      Download Template
                    </button>
                  </div>
                </div>

                {/* Step 2: Upload File */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold text-sm">
                      2
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Upload File
                    </h3>
                  </div>
                  <div className="ml-11 space-y-3">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Fill in the template and upload your completed Excel file
                    </p>
                    <div
                      onClick={() => {
                        if (!importing) fileInputRef.current?.click();
                      }}
                      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        importing
                          ? "border-gray-300 dark:border-gray-600 cursor-not-allowed bg-gray-50 dark:bg-gray-700/50"
                          : importFile
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                            : "border-gray-300 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      }`}
                    >
                      {importFile ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-center text-green-600 dark:text-green-400">
                            <FileText className="w-12 h-12" />
                          </div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {importFile.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {(importFile.size / 1024).toFixed(2)} KB
                          </p>
                          {!importing && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setImportFile(null);
                                setImportError(null);
                              }}
                              className="text-xs text-red-600 dark:text-red-400 hover:underline"
                            >
                              Remove file
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center justify-center text-gray-400">
                            <Upload className="w-12 h-12" />
                          </div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Excel files (.xlsx, .xls)
                          </p>
                        </div>
                      )}
                    </div>
                    {importError && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {importError}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 3: Import Options */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-green-600 text-white font-bold text-sm">
                      3
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Import Options
                    </h3>
                  </div>
                  <div className="ml-11 space-y-3">
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={replaceExisting}
                          onChange={(e) => setReplaceExisting(e.target.checked)}
                          disabled={importing}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Update existing inventory items
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                        If unchecked, existing items will be skipped
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={clearBeforeImport}
                          onChange={(e) =>
                            setClearBeforeImport(e.target.checked)
                          }
                          disabled={importing}
                          className="w-4 h-4 text-red-600 rounded focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Clear existing inventory before import
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                        ⚠️ This will delete all existing inventory data
                      </p>
                    </div>
                  </div>
                </div>

                {/* Import Progress */}
                {importing && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
                      <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                        Importing... {importProgress.current} /{" "}
                        {importProgress.total}
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 dark:bg-blue-900 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${(importProgress.current / importProgress.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Import Results */}
                {!importing && importProgress.total > 0 && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {importProgress.created}
                        </p>
                        <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                          Created
                        </p>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {importProgress.updated}
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          Updated
                        </p>
                      </div>
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                          {importProgress.skipped}
                        </p>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                          Skipped
                        </p>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                          {importProgress.errors}
                        </p>
                        <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                          Errors
                        </p>
                      </div>
                    </div>

                    {/* Results Tabs */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div className="flex border-b border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => setActiveResultTab("success")}
                          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                            activeResultTab === "success"
                              ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-b-2 border-green-600"
                              : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                        >
                          ✅ Success ({importProgress.successDetails.length})
                        </button>
                        <button
                          onClick={() => setActiveResultTab("errors")}
                          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                            activeResultTab === "errors"
                              ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-b-2 border-red-600"
                              : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                        >
                          ❌ Errors ({importProgress.errorDetails.length})
                        </button>
                      </div>

                      <div className="max-h-64 overflow-y-auto">
                        {activeResultTab === "success" && (
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                  Row
                                </th>
                                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                  Item Code
                                </th>
                                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                  SKU Code
                                </th>
                                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                  Item Name
                                </th>
                                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {importProgress.successDetails.length > 0 ? (
                                importProgress.successDetails.map(
                                  (item, idx) => (
                                    <tr
                                      key={idx}
                                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                        {item.row}
                                      </td>
                                      <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                                        {item.itemCode}
                                      </td>
                                      <td className="px-3 py-2 text-gray-900 dark:text-white">
                                        {item.skuCode}
                                      </td>
                                      <td className="px-3 py-2 text-gray-900 dark:text-white">
                                        {item.itemName}
                                      </td>
                                      <td className="px-3 py-2">
                                        <span
                                          className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                                            item.action === "Created"
                                              ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                                              : item.action === "Updated"
                                                ? "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                                                : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                                          }`}
                                        >
                                          {item.action}
                                        </span>
                                      </td>
                                    </tr>
                                  ),
                                )
                              ) : (
                                <tr>
                                  <td
                                    colSpan="5"
                                    className="px-3 py-8 text-center text-gray-500 dark:text-gray-400"
                                  >
                                    No successful imports
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        )}

                        {activeResultTab === "errors" && (
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                  Row
                                </th>
                                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                  Item Code
                                </th>
                                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                  SKU Code
                                </th>
                                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                  Item Name
                                </th>
                                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                  Error
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {importProgress.errorDetails.length > 0 ? (
                                importProgress.errorDetails.map((item, idx) => (
                                  <tr
                                    key={idx}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                      {item.row}
                                    </td>
                                    <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                                      {item.itemCode}
                                    </td>
                                    <td className="px-3 py-2 text-gray-900 dark:text-white">
                                      {item.skuCode}
                                    </td>
                                    <td className="px-3 py-2 text-gray-900 dark:text-white">
                                      {item.itemName}
                                    </td>
                                    <td className="px-3 py-2 text-red-600 dark:text-red-400">
                                      {item.error}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan="5"
                                    className="px-3 py-8 text-center text-gray-500 dark:text-gray-400"
                                  >
                                    No errors
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    if (!importing) {
                      setImportModalOpen(false);
                      setImportFile(null);
                      setImportError(null);
                      setImportProgress({
                        total: 0,
                        current: 0,
                        success: 0,
                        created: 0,
                        updated: 0,
                        skipped: 0,
                        errors: 0,
                        errorDetails: [],
                        successDetails: [],
                      });
                    }
                  }}
                  disabled={importing}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importProgress.total > 0 ? "Close" : "Cancel"}
                </button>
                {!importing && importFile && (
                  <button
                    onClick={handleImport}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Start Import
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Initialize Modal */}
      {bulkInitModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setBulkInitModalOpen(false)}
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full mx-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  🚀 Auto Inventory Setup
                </h2>
                <button
                  onClick={() => setBulkInitModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {bulkInitLoading && !skusWithoutInventory ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                    <span className="ml-3 text-gray-600 dark:text-gray-400">
                      Loading SKU data...
                    </span>
                  </div>
                ) : skusWithoutInventory ? (
                  <>
                    <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="text-blue-700 dark:text-blue-300 font-medium mb-2">
                        📊 Inventory Status
                      </p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">
                            Total SKUs
                          </p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {skusWithoutInventory.total}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">
                            With Inventory
                          </p>
                          <p className="text-lg font-bold text-green-600 dark:text-green-400">
                            {skusWithoutInventory.withInventory}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-600 dark:text-gray-400">
                            Without Inventory
                          </p>
                          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                            {skusWithoutInventory.withoutInventory}
                          </p>
                        </div>
                      </div>
                    </div>

                    {skusWithoutInventory.withoutInventory > 0 ? (
                      <>
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                          <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                            ⚡ This will automatically create inventory entries
                            with <strong>0 quantity</strong> for{" "}
                            {skusWithoutInventory.withoutInventory} SKUs. You
                            can then update quantities via stock movements or
                            the edit function.
                          </p>
                        </div>

                        {skusWithoutInventory.skus.length > 0 && (
                          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                    SKU
                                  </th>
                                  <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">
                                    Product
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {skusWithoutInventory.skus
                                  .slice(0, 20)
                                  .map((sku) => (
                                    <tr
                                      key={sku.id}
                                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                      <td className="px-3 py-2 text-gray-900 dark:text-white font-mono text-xs">
                                        {sku.sku}
                                      </td>
                                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                        {sku.productName}
                                      </td>
                                    </tr>
                                  ))}
                                {skusWithoutInventory.skus.length > 20 && (
                                  <tr>
                                    <td
                                      colSpan="2"
                                      className="px-3 py-2 text-center text-gray-500 dark:text-gray-400 text-sm"
                                    >
                                      ... and{" "}
                                      {skusWithoutInventory.skus.length - 20}{" "}
                                      more
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                        <p className="text-green-700 dark:text-green-300 font-medium">
                          All SKUs already have inventory entries!
                        </p>
                      </div>
                    )}
                  </>
                ) : null}
              </div>

              <div className="flex justify-end gap-2 p-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  onClick={() => setBulkInitModalOpen(false)}
                  variant="outline"
                  disabled={bulkInitLoading}
                >
                  Cancel
                </Button>
                {skusWithoutInventory?.withoutInventory > 0 && (
                  <Button
                    onClick={handleBulkInitialize}
                    variant="primary"
                    disabled={bulkInitLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {bulkInitLoading ? (
                      <>
                        <LoadingSpinner className="w-4 h-4 mr-2" />
                        Initializing...
                      </>
                    ) : (
                      <>
                        <Boxes className="w-4 h-4 mr-2" />
                        Initialize {skusWithoutInventory.withoutInventory} SKUs
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <input
        type="file"
        accept=".xlsx,.xls"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search inventory..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-colors ${
                showFilters || hasActiveFilters
                  ? "border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="flex items-center justify-center w-5 h-5 bg-blue-600 text-white text-xs rounded-full">
                  {(locationFilter ? 1 : 0) +
                    (supplierFilter ? 1 : 0) +
                    (statusFilter ? 1 : 0)}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          {/* Collapsible Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    Location
                  </label>
                  <select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="">All Locations</option>
                    {getUniqueLocations().map((loc) => (
                      <option key={loc} value={loc}>
                        {loc}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    Supplier
                  </label>
                  <select
                    value={supplierFilter}
                    onChange={(e) => setSupplierFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="">All Suppliers</option>
                    {getUniqueSuppliers().map((sup) => (
                      <option key={sup} value={sup}>
                        {sup}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="">All Status</option>
                    <option value="good">Good Condition</option>
                    <option value="low_stock">Low Stock</option>
                    <option value="expired">Expired</option>
                    <option value="expiring_soon">Expiring Soon</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions (shown when items are selected) */}
        {selectedItems.length > 0 && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-3">
              <span className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                {selectedItems.length} items selected
              </span>
              <button
                onClick={handleBulkAddStock}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <TrendingUp className="w-4 h-4" />
                Bulk Add Stock
              </button>
              <button
                onClick={() => setSelectedItems([])}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-sm font-medium rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {importedRows.length > 0 && (
          <div className="mb-4 border p-4 bg-yellow-50 rounded">
            <div className="font-bold mb-2">Preview Imported Data</div>
            <table className="w-full border border-gray-300 mb-2">
              <thead className="bg-blue-50">
                <tr>
                  {TEMPLATE_HEADERS.map((h) => (
                    <th key={h} className="border px-2 py-1">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {importedRows.map((row, i) => {
                  // Extract data using new column names
                  const itemCode = row["Item Code*"] || "";
                  const skuCode = row["SKU Code*"] || "";
                  const barcode = row["Barcode"] || "";
                  const itemName = row["Item Name*"] || "";
                  const quantity = row["Quantity Received*"] || "";
                  const location = row["Receiving Location"] || "";
                  const batch = row["Batch Number"] || "";
                  const expiry = row["Expiry Date"] || "";

                  // Find product and SKU using the same logic as handleImportAll
                  let product = null;
                  let productSku = null;
                  let lookupMethod = "";

                  // Method 1: Try to find by SKU code directly
                  if (skuCode && skuCode.trim() !== "") {
                    for (const p of products) {
                      const foundSku = p.skus?.find(
                        (sku) => sku.sku === skuCode.trim(),
                      );
                      if (foundSku) {
                        productSku = foundSku;
                        product = p;
                        lookupMethod = "SKU Code";
                        break;
                      }
                    }
                  }

                  // Method 2: Try to find by barcode
                  if (!productSku && barcode && barcode.trim() !== "") {
                    for (const p of products) {
                      const foundSku = p.skus?.find(
                        (sku) => sku.barcode === barcode.trim(),
                      );
                      if (foundSku) {
                        productSku = foundSku;
                        product = p;
                        lookupMethod = "Barcode";
                        break;
                      }
                    }
                  }

                  // Method 3: Try to find by product item code
                  if (!product && itemCode && itemCode.trim() !== "") {
                    product = products.find((p) => p.itemCode === itemCode);
                    if (product) {
                      if (skuCode && skuCode.trim() !== "") {
                        productSku = product.skus?.find(
                          (sku) => sku.sku === skuCode.trim(),
                        );
                        if (productSku) {
                          lookupMethod = "Product Item Code + SKU Code";
                        }
                      }

                      if (!productSku && product.skus?.length > 0) {
                        if (itemName && itemName.trim() !== "") {
                          productSku = product.skus?.find(
                            (sku) =>
                              sku.name
                                .toLowerCase()
                                .includes(itemName.toLowerCase()) ||
                              itemName
                                .toLowerCase()
                                .includes(sku.name.toLowerCase()),
                          );
                          if (productSku) {
                            lookupMethod =
                              "Product Item Code + Item Name Match";
                          }
                        }

                        if (!productSku) {
                          productSku = product.skus[0];
                          lookupMethod = "Product Item Code + First SKU";
                        }
                      }
                    }
                  }

                  const parsedExpiry = parseExcelDate(expiry);
                  const isValid = product && productSku;

                  return (
                    <tr key={i} className={isValid ? "" : "bg-red-100"}>
                      <td className="border px-2 py-1">{itemCode || ""}</td>
                      <td className="border px-2 py-1">{skuCode || ""}</td>
                      <td className="border px-2 py-1">{barcode || ""}</td>
                      <td className="border px-2 py-1">{itemName || ""}</td>
                      <td className="border px-2 py-1">
                        {row["Brand / Manufacturer"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["HSN / Tariff Code"] || ""}
                      </td>
                      <td className="border px-2 py-1">{quantity || ""}</td>
                      <td className="border px-2 py-1">
                        {row["Unit of Measure (UOM)"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Pack Size"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Date Received"] || ""}
                      </td>
                      <td className="border px-2 py-1">{location || ""}</td>
                      <td className="border px-2 py-1">
                        {row["Received By"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Supplier Delivery Note / GRN No."] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Our Purchase Order Number"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Supplier Name"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Supplier Invoice Number"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Supplier Invoice Date"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Unit Cost"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Currency"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Tax Rate (%)"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Total Procurement Value"] || ""}
                      </td>
                      <td className="border px-2 py-1">{batch || ""}</td>
                      <td className="border px-2 py-1">
                        {row["Manufacturing Date"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {parsedExpiry
                          ? parsedExpiry.toLocaleDateString()
                          : expiry || "N/A"}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Stock Status"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Storage Conditions"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Bin / Rack Location"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Procurement Remarks"] || ""}
                      </td>
                      <td className="border px-2 py-1">
                        {row["Attachments"] || ""}
                      </td>
                      <td className="border px-2 py-1 text-xs text-gray-500">
                        {isValid
                          ? `✅ ${lookupMethod}`
                          : "❌ Product/SKU not found"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button
              className="bg-green-700 text-white px-4 py-1 rounded hover:bg-green-800"
              onClick={handleImportAll}
              disabled={
                importing ||
                importedRows.some((row) => {
                  const itemCode = row["Item Code*"] || "";
                  const itemName = row["Item Name*"] || "";

                  if (!itemCode || itemCode.trim() === "") return true;

                  const product = products.find((p) => p.itemCode === itemCode);
                  if (!product) return true;

                  // Check if we can find a matching SKU
                  if (
                    itemName &&
                    itemName.trim() !== "" &&
                    product.skus?.length > 0
                  ) {
                    const matchingSku = product.skus?.find(
                      (sku) =>
                        sku.name
                          .toLowerCase()
                          .includes(itemName.toLowerCase()) ||
                        itemName.toLowerCase().includes(sku.name.toLowerCase()),
                    );
                    if (matchingSku) return false;
                  }

                  // If no specific SKU match, check if product has any SKUs
                  return !product.skus || product.skus.length === 0;
                })
              }
            >
              {importing ? "Importing..." : "Import All"}
            </button>
            <button
              className="bg-gray-500 text-white px-4 py-1 rounded hover:bg-gray-700 ml-2"
              onClick={() => {
                setImportedRows([]);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            >
              Cancel Import
            </button>
            {importError && (
              <div className="text-red-600 mt-2">{importError}</div>
            )}
            {importedRows.some((row) => {
              const itemCode = row["Item Code*"] || "";
              const skuCode =
                row["SKU Code*"] || row["SKU Code"] || row["SKU"] || "";
              const barcode = row["Barcode"] || "";
              const itemName = row["Item Name*"] || "";

              // Skip empty rows
              if (!itemCode && !skuCode && !barcode) return false;

              let product = null;
              let productSku = null;

              // Method 1: Try to find by SKU code directly
              if (skuCode && skuCode.trim() !== "") {
                for (const p of products) {
                  const foundSku = p.skus?.find(
                    (sku) => sku.sku === skuCode.trim(),
                  );
                  if (foundSku) {
                    productSku = foundSku;
                    product = p;
                    break;
                  }
                }
              }

              // Method 2: Try to find by barcode
              if (!productSku && barcode && barcode.trim() !== "") {
                for (const p of products) {
                  const foundSku = p.skus?.find(
                    (sku) => sku.barcode === barcode.trim(),
                  );
                  if (foundSku) {
                    productSku = foundSku;
                    product = p;
                    break;
                  }
                }
              }

              // Method 3: Try to find by product item code
              if (!product && itemCode && itemCode.trim() !== "") {
                product = products.find((p) => p.itemCode === itemCode);
                if (product) {
                  if (skuCode && skuCode.trim() !== "") {
                    productSku = product.skus?.find(
                      (sku) => sku.sku === skuCode.trim(),
                    );
                  }

                  if (!productSku && product.skus?.length > 0) {
                    if (itemName && itemName.trim() !== "") {
                      productSku = product.skus?.find(
                        (sku) =>
                          sku.name
                            .toLowerCase()
                            .includes(itemName.toLowerCase()) ||
                          itemName
                            .toLowerCase()
                            .includes(sku.name.toLowerCase()),
                      );
                    }

                    if (!productSku) {
                      productSku = product.skus[0];
                    }
                  }
                }
              }

              // Return true if we couldn't find a valid product and SKU
              return !product || !productSku;
            }) && (
              <div className="text-red-600 mt-2">
                Some rows have invalid Product Code/SKU and cannot be imported.
              </div>
            )}
          </div>
        )}
        {/* Inventory Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedItems(paginatedItems.map((item) => item.id));
                      } else {
                        setSelectedItems([]);
                      }
                    }}
                    checked={
                      selectedItems.length === paginatedItems.length &&
                      paginatedItems.length > 0
                    }
                  />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("productName")}
                >
                  <div className="flex items-center gap-1">
                    Product
                    {sortConfig.key === "productName" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("supplier")}
                >
                  <div className="flex items-center gap-1">
                    Supplier
                    {sortConfig.key === "supplier" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("location")}
                >
                  <div className="flex items-center gap-1">
                    Location
                    {sortConfig.key === "location" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("batch")}
                >
                  <div className="flex items-center gap-1">
                    Batch
                    {sortConfig.key === "batch" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("quantity")}
                >
                  <div className="flex items-center justify-end gap-1">
                    Quantity
                    {sortConfig.key === "quantity" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("expiryDate")}
                >
                  <div className="flex items-center justify-center gap-1">
                    Expiry
                    {sortConfig.key === "expiryDate" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedItems.map((item) => {
                const isExpired =
                  item.expiryDate && new Date(item.expiryDate) < new Date();
                const isExpiringSoon =
                  item.expiryDate &&
                  !isExpired &&
                  new Date(item.expiryDate) <=
                    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                const isLowStock =
                  item.minStock && item.quantity <= item.minStock;

                return (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-4 py-4 text-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        checked={selectedItems.includes(item.id)}
                        onChange={(e) =>
                          handleItemSelection(item.id, e.target.checked)
                        }
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          <Package className="w-5 h-5 text-gray-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.productSku?.name ||
                              item.productSku?.product?.name ||
                              "-"}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {item.productSku?.product?.itemCode &&
                              `${item.productSku.product.itemCode}`}
                            {item.productSku?.sku &&
                              ` • ${item.productSku.sku}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {item.supplier?.name || "-"}
                      </div>
                      {item.supplier?.code && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.supplier.code}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {item.location || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {item.batch || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span
                          className={`text-sm font-semibold ${
                            isLowStock
                              ? "text-red-600 dark:text-red-400"
                              : "text-gray-900 dark:text-white"
                          }`}
                        >
                          {item.quantity}
                        </span>
                        {isLowStock && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800">
                            Low
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                          isExpired
                            ? "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800"
                            : isExpiringSoon
                              ? "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800"
                              : "bg-gray-50 text-gray-700 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                        }`}
                      >
                        {item.expiryDate
                          ? new Date(item.expiryDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleViewDetails(item)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAddStock(item)}
                              className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-colors"
                              title="Add Stock"
                            >
                              <TrendingUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveStock(item)}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Remove Stock"
                            >
                              <TrendingDown className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              {sortedItems.length > 0 ? indexOfFirstItem + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              {Math.min(indexOfLastItem, sortedItems.length)}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              {sortedItems.length}
            </span>{" "}
            results
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg transition-colors ${
                  currentPage === 1
                    ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={`min-w-[2rem] h-8 px-3 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === pageNumber
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`p-2 rounded-lg transition-colors ${
                  currentPage === totalPages || totalPages === 0
                    ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Empty State */}
      {paginatedItems.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full">
              <Boxes className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No inventory items found
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {hasActiveFilters
              ? "Try adjusting your search or filter criteria"
              : "Get started by adding your first inventory item"}
          </p>
          {hasActiveFilters ? (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 dark:text-blue-400 text-sm font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Clear all filters
            </button>
          ) : (
            isAdmin && (
              <button
                onClick={() => navigate("/admin/inventory/add")}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 transition-all duration-200"
              >
                <Plus className="w-4 h-4" />
                Add Inventory
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default InventoryDashboard;
